import { useState } from 'react';
import { cashApi, lookupsApi } from '../../api/services';
import type { CashCloseResultDto, CashMovementDto, CashOpeningDto, CashRegisterDetailDto, CashSummaryRowDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Select, Stat, Table, Tabs, Toolbar,
  fmtDateTime, fmtMoney, money, useAction, useAsync,
} from '../../ui/components';
import { TemplateSelect, useCurrencies } from '../../ui/pickers';
import { PageHelp } from '../../ui/help';

export function CashPage() {
  const registers = useAsync(() => cashApi.registers(), []);
  const [selected, setSelected] = useState<number | null>(null);
  return (
    <>
      <PageHeader title="Caja" subtitle="Cajas del punto de venta (se administran en el POS). Aquí se manejan usuarios asignados, aperturas, ingresos, egresos, arqueo y cierre." />
      <PageHelp page="cash" />
      <Loadable state={registers}>
        {(rows) => (
          <Table rows={rows} rowKey={(r) => r.id} selected={selected} onRowClick={(r) => setSelected(r.id)} empty="La empresa no tiene cajas registradas en el punto de venta." columns={[
            { key: 'i', title: 'Caja', render: (r) => r.id },
            { key: 'n', title: 'Nombre', render: (r) => r.name },
            { key: 'a', title: 'Activa', render: (r) => (r.active ? 'Sí' : 'No') },
            { key: 'e', title: 'Estado', render: (r) => <Badge tone={r.isOpen ? 'ok' : 'neutral'}>{r.isOpen ? `Abierta (${r.openedBy ?? ''})` : 'Cerrada'}</Badge> },
            { key: 'u', title: 'Usuarios', render: (r) => r.users.join(', ') },
          ]} />
        )}
      </Loadable>
      {selected !== null && <RegisterPanel id={selected} onChanged={registers.reload} />}
    </>
  );
}

type PanelTab = 'current' | 'openings' | 'users';

function RegisterPanel({ id, onChanged }: { id: number; onChanged: () => void }) {
  const reg = useAsync(() => cashApi.register(id), [id]);
  const [tab, setTab] = useState<PanelTab>('current');
  const reload = () => { reg.reload(); onChanged(); };
  return (
    <Loadable state={reg}>
      {(r) => (
        <Card title={`Caja ${r.id} · ${r.name}`}>
          <Tabs<PanelTab> tabs={[{ key: 'current', label: 'Apertura actual' }, { key: 'openings', label: 'Historial' }, { key: 'users', label: 'Usuarios' }]} active={tab} onChange={setTab} />
          {tab === 'current' && <CurrentOpening register={r} onChanged={reload} />}
          {tab === 'openings' && <OpeningsHistory registerId={r.id} />}
          {tab === 'users' && <UsersTab register={r} onChanged={reload} />}
        </Card>
      )}
    </Loadable>
  );
}

function CurrentOpening({ register, onChanged }: { register: CashRegisterDetailDto; onChanged: () => void }) {
  const [opening, setOpening] = useState(false);
  const [closing, setClosing] = useState(false);
  const [moving, setMoving] = useState<'income' | 'expense' | null>(null);
  const [closed, setClosed] = useState<CashCloseResultDto | null>(null);
  if (!register.isOpen || !register.openingId) {
    return (
      <>
        {closed && <CloseResult result={closed} />}
        <Alert>La caja no tiene una apertura abierta.</Alert>
        <Button kind="primary" disabled={!register.active} onClick={() => setOpening(true)}>Abrir caja</Button>
        {opening && <OpenForm registerId={register.id} onClose={() => setOpening(false)} onSaved={() => { setOpening(false); setClosed(null); onChanged(); }} />}
      </>
    );
  }
  const openingId = register.openingId;
  return (
    <>
      <Toolbar>
        <span>Apertura {openingId} · {register.openedBy} · {fmtDateTime(register.openedOn)}</span>
        <Button onClick={() => setMoving('income')}>Ingreso</Button>
        <Button onClick={() => setMoving('expense')}>Egreso</Button>
        <Button kind="primary" onClick={() => setClosing(true)}>Cerrar caja</Button>
      </Toolbar>
      <OpeningDetail openingId={openingId} editable onChanged={onChanged} />
      {moving && <MovementForm kind={moving} registerId={register.id} onClose={() => setMoving(null)} onSaved={() => { setMoving(null); onChanged(); }} />}
      {closing && <CloseForm registerId={register.id} openingId={openingId} onClose={() => setClosing(false)} onSaved={(r) => { setClosing(false); setClosed(r); onChanged(); }} />}
    </>
  );
}

function OpeningDetail({ openingId, editable, onChanged }: { openingId: number; editable?: boolean; onChanged?: () => void }) {
  const count = useAsync(() => cashApi.count(openingId), [openingId]);
  const moves = useAsync(() => cashApi.movements(openingId), [openingId]);
  const summary = useAsync(() => cashApi.summary(openingId), [openingId]);
  const currencies = useCurrencies();
  const { run, busy } = useAction();
  const cur = (id: number) => currencies.data?.find((c) => c.id === id)?.iso ?? String(id);
  return (
    <>
      <Loadable state={count}>
        {(c) => (
          <div className="acc-stats">
            <Stat label="Fondo inicial (base / dólares)" value={`${fmtMoney(c.openingLocal)} / ${fmtMoney(c.openingDollar)}`} />
            <Stat label="Ingresos + cobros efectivo" value={`${fmtMoney(c.incomeLocal + c.collectionsLocal)} / ${fmtMoney(c.incomeDollar + c.collectionsDollar)}`} />
            <Stat label="Egresos" value={`${fmtMoney(c.expenseLocal)} / ${fmtMoney(c.expenseDollar)}`} />
            <Stat label="Efectivo esperado" value={`${fmtMoney(c.expectedLocal)} / ${fmtMoney(c.expectedDollar)}`} />
            <Stat label="Cobros no efectivo (base)" value={fmtMoney(c.nonCashCollectionsLocal)} />
          </div>
        )}
      </Loadable>
      <h4>Movimientos</h4>
      <Loadable state={moves}>
        {(rows) => (
          <Table rows={rows} rowKey={(m) => m.id} empty="Sin movimientos." columns={[
            { key: 'f', title: 'Fecha', render: (m) => fmtDateTime(m.date) },
            { key: 't', title: 'Tipo', render: (m) => m.type },
            { key: 'd', title: 'Detalle', render: (m) => m.description },
            { key: 'p', title: 'Forma', render: (m) => (m.paymentMethod ? `${m.paymentMethod}${m.isCash ? '' : ' (no efectivo)'}` : '') },
            { key: 'c', title: 'Moneda', render: (m) => cur(m.currencyId) },
            money<CashMovementDto>('m', 'Monto', (m) => m.amount),
            money<CashMovementDto>('ml', 'Monto base', (m) => m.amountLocal),
            { key: 'u', title: 'Usuario', render: (m) => m.user },
            { key: 'a', title: 'Asiento', render: (m) => m.entryCode ?? (m.paymentNumber ? `cobro ${m.paymentNumber}` : '') },
            { key: 'e', title: 'Estado', render: (m) => <Badge tone={m.status === 'A' ? 'ok' : 'bad'}>{m.status === 'A' ? 'Aplicado' : 'Anulado'}</Badge> },
            {
              key: 'x', title: '', render: (m) => (editable && m.status === 'A' && m.type !== 'COBRO' ? (
                <Button kind="link" busy={busy} onClick={() => run((reason) => cashApi.cancelMovement(m.id, reason ?? ''), {
                  confirm: `¿Anular el ${m.type.toLowerCase()} ${m.id}? Se anula su asiento con un reverso.`, danger: true, requireReason: true, success: 'Movimiento anulado.',
                }).then(() => { moves.reload(); count.reload(); summary.reload(); onChanged?.(); })}>Anular</Button>
              ) : null),
            },
          ]} />
        )}
      </Loadable>
      <h4>Resumen</h4>
      <Loadable state={summary}>
        {(rows) => (
          <Table rows={rows.map((r, i) => ({ ...r, i }))} rowKey={(r) => r.i} columns={[
            { key: 't', title: 'Tipo', render: (r) => r.type },
            { key: 'p', title: 'Forma', render: (r) => r.paymentMethod ?? '' },
            { key: 'e', title: 'Efectivo', render: (r) => (r.isCash ? 'Sí' : 'No') },
            { key: 'c', title: 'Moneda', render: (r) => cur(r.currencyId) },
            { key: 'n', title: 'Cantidad', align: 'right', render: (r) => r.count },
            money<CashSummaryRowDto & { i: number }>('m', 'Monto', (r) => r.amount),
            money<CashSummaryRowDto & { i: number }>('ml', 'Monto base', (r) => r.amountLocal),
          ]} />
        )}
      </Loadable>
    </>
  );
}

function OpenForm({ registerId, onClose, onSaved }: { registerId: number; onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState({ local: 0, dollar: 0 });
  const { run, busy } = useAction();
  return (
    <Modal title={`Abrir caja ${registerId}`} onClose={onClose} size="sm">
      <Form onSubmit={() => run(async () => { await cashApi.open(registerId, v.local, v.dollar); onSaved(); }, { success: 'Caja abierta.' })}>
        <Field label="Fondo inicial (moneda base)"><NumberInput value={v.local} onChange={(x) => setV({ ...v, local: x })} min={0} /></Field>
        <Field label="Fondo inicial (dólares)"><NumberInput value={v.dollar} onChange={(x) => setV({ ...v, dollar: x })} min={0} /></Field>
        <Alert>La apertura no genera asiento: el fondo ya está en la cuenta de caja.</Alert>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Abrir</Button></div>
      </Form>
    </Modal>
  );
}

function MovementForm({ kind, registerId, onClose, onSaved }: { kind: 'income' | 'expense'; registerId: number; onClose: () => void; onSaved: () => void }) {
  const company = useAsync(() => lookupsApi.company(), []);
  const currencies = useCurrencies();
  const [v, setV] = useState({ currencyId: 0, amount: 0, exchangeRate: null as number | null, description: '', reference: '', templateCode: null as number | null });
  const { run, busy } = useAction();
  const currencyId = v.currencyId || company.data?.company.defaultCurrencyId || 0;
  return (
    <Modal title={kind === 'income' ? 'Ingreso de caja' : 'Egreso de caja'} onClose={onClose}>
      <Form onSubmit={() => run(async () => {
        const dto = { cashRegisterId: registerId, currencyId, amount: v.amount, exchangeRate: v.exchangeRate, description: v.description.trim(), reference: v.reference.trim() || null, templateCode: v.templateCode };
        await (kind === 'income' ? cashApi.income(dto) : cashApi.expense(dto));
        onSaved();
      }, { success: 'Movimiento registrado con su asiento.' })}>
        <div className="acc-grid-form">
          <Field label="Moneda"><Select value={currencyId || ''} onChange={(x) => setV({ ...v, currencyId: x })} options={(currencies.data ?? []).filter((c) => c.mapped).map((c) => ({ value: c.id, label: c.iso ?? c.name }))} /></Field>
          <Field label="Monto"><NumberInput value={v.amount} onChange={(x) => setV({ ...v, amount: x })} min={0} /></Field>
          <Field label="Tipo de cambio" hint="Sólo moneda extranjera; vacío = tipo de cambio del día"><NumberInput step="0.000001" value={v.exchangeRate} onChange={(x) => setV({ ...v, exchangeRate: x || null })} /></Field>
          <Field label="Referencia"><Input value={v.reference} onChange={(x) => setV({ ...v, reference: x })} maxLength={100} /></Field>
          <Field label="Plantilla"><TemplateSelect moduleCode="CJ" value={v.templateCode} onChange={(x) => setV({ ...v, templateCode: x })} /></Field>
        </div>
        <Field label="Descripción" wide><Input value={v.description} onChange={(x) => setV({ ...v, description: x })} maxLength={300} required /></Field>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={v.amount <= 0 || !v.description.trim() || !currencyId}>Registrar</Button></div>
      </Form>
    </Modal>
  );
}

function CloseForm({ registerId, openingId, onClose, onSaved }: { registerId: number; openingId: number; onClose: () => void; onSaved: (r: CashCloseResultDto) => void }) {
  const count = useAsync(() => cashApi.count(openingId), [openingId]);
  const [v, setV] = useState({ countedLocal: 0, countedDollar: 0, exchangeRate: null as number | null, closingNote: '' });
  const { run, busy } = useAction();
  return (
    <Modal title={`Cerrar caja ${registerId}`} onClose={onClose}>
      <Loadable state={count}>
        {(c) => (
          <Form onSubmit={() => run(async () => { const r = await cashApi.close(registerId, { ...v, closingNote: v.closingNote.trim() || null }); onSaved(r); }, { success: 'Caja cerrada.' })}>
            <div className="acc-grid-form">
              <Field label="Esperado (base)">{fmtMoney(c.expectedLocal)}</Field>
              <Field label="Contado (base)"><NumberInput value={v.countedLocal} onChange={(x) => setV({ ...v, countedLocal: x })} min={0} /></Field>
              <Field label="Esperado (dólares)">{fmtMoney(c.expectedDollar)}</Field>
              <Field label="Contado (dólares)"><NumberInput value={v.countedDollar} onChange={(x) => setV({ ...v, countedDollar: x })} min={0} /></Field>
              <Field label="Tipo de cambio" hint="Para valorar la diferencia en dólares"><NumberInput step="0.000001" value={v.exchangeRate} onChange={(x) => setV({ ...v, exchangeRate: x || null })} /></Field>
            </div>
            <Field label="Nota de cierre" wide><Input value={v.closingNote} onChange={(x) => setV({ ...v, closingNote: x })} maxLength={500} /></Field>
            <Alert>Diferencia: {fmtMoney(v.countedLocal - c.expectedLocal)} (base) · {fmtMoney(v.countedDollar - c.expectedDollar)} (dólares). Una diferencia genera asiento de faltante/sobrante si la plantilla está configurada.</Alert>
            <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Cerrar caja</Button></div>
          </Form>
        )}
      </Loadable>
    </Modal>
  );
}

function CloseResult({ result }: { result: CashCloseResultDto }) {
  return (
    <Card title={`Cierre de la apertura ${result.opening.openingId}`}>
      <div className="acc-stats">
        <Stat label="Diferencia (base)" value={fmtMoney(result.differenceLocal)} tone={result.differenceLocal < 0 ? 'neg' : undefined} />
        <Stat label="Diferencia (dólares)" value={fmtMoney(result.differenceDollar)} tone={result.differenceDollar < 0 ? 'neg' : undefined} />
        <Stat label="Diferencia neta (base)" value={fmtMoney(result.differenceBase)} tone={result.differenceBase < 0 ? 'neg' : undefined} />
        <Stat label="Asiento de cierre" value={result.closingEntryCode ?? '—'} />
      </div>
      {result.warning && <Alert kind="warn">{result.warning}</Alert>}
    </Card>
  );
}

function OpeningsHistory({ registerId }: { registerId: number }) {
  const list = useAsync(() => cashApi.openings(registerId), [registerId]);
  const [selected, setSelected] = useState<number | null>(null);
  return (
    <>
      <Loadable state={list}>
        {(rows) => (
          <Table rows={rows} rowKey={(o) => o.openingId} selected={selected} onRowClick={(o) => setSelected(o.openingId)} columns={[
            { key: 'i', title: 'Apertura', render: (o) => o.openingId },
            { key: 'a', title: 'Abierta', render: (o) => `${fmtDateTime(o.openedOn)} · ${o.openedBy}` },
            { key: 'c', title: 'Cerrada', render: (o) => (o.closedOn ? `${fmtDateTime(o.closedOn)} · ${o.closedBy ?? ''}` : '') },
            money<CashOpeningDto>('e', 'Esperado base', (o) => o.expectedLocal),
            money<CashOpeningDto>('k', 'Contado base', (o) => o.countedLocal),
            { key: 's', title: 'Estado', render: (o) => <Badge tone={o.status === 'A' ? 'ok' : 'neutral'}>{o.status === 'A' ? 'Abierta' : 'Cerrada'}</Badge> },
            { key: 'x', title: 'Asiento cierre', render: (o) => o.closingEntryCode ?? '' },
          ]} />
        )}
      </Loadable>
      {selected !== null && <OpeningDetail openingId={selected} />}
    </>
  );
}

function UsersTab({ register, onChanged }: { register: CashRegisterDetailDto; onChanged: () => void }) {
  const [user, setUser] = useState('');
  const { run, busy } = useAction();
  return (
    <>
      <Toolbar>
        <Input value={user} onChange={setUser} placeholder="Usuario (nombre de usuario del token)" maxLength={100} />
        <Button kind="primary" busy={busy} disabled={!user.trim()} onClick={() => run(() => cashApi.addUser(register.id, user.trim()), { success: 'Usuario asignado.' }).then(() => { setUser(''); onChanged(); })}>Asignar</Button>
      </Toolbar>
      <Table rows={register.users.map((u) => ({ u }))} rowKey={(r) => r.u} empty="Sin usuarios asignados." columns={[
        { key: 'u', title: 'Usuario', render: (r) => r.u },
        { key: 'x', title: '', render: (r) => <Button kind="link" busy={busy} onClick={() => run(() => cashApi.removeUser(register.id, r.u), { confirm: `¿Quitar a ${r.u} de la caja?`, success: 'Usuario removido.' }).then(onChanged)}>Quitar</Button> },
      ]} />
    </>
  );
}
